// Sentry error tracking (frontend)
import * as Sentry from "@sentry/browser";

const sentryDsn = document.querySelector("meta[name='sentry-dsn']")?.getAttribute("content");
const sentryEnv = document.querySelector("meta[name='sentry-environment']")?.getAttribute("content");

if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: sentryEnv || "production",
  });
}

// If you want to use Phoenix channels, run `mix help phx.gen.channel`
// to get started and then uncomment the line below.
// import "./user_socket.js"

// You can include dependencies in two ways.
//
// The simplest option is to put them in assets/vendor and
// import them using relative paths:
//
//     import "../vendor/some-package.js"
//
// Alternatively, you can `npm install some-package --prefix assets` and import
// them using a path starting with the package name:
//
//     import "some-package"
//
// If you have dependencies that try to import CSS, esbuild will generate a separate `app.css` file.
// To load it, simply add a second `<link>` to your `root.html.heex` file.

// Include phoenix_html to handle method=PUT/DELETE in forms and buttons.
import "phoenix_html";
// Establish Phoenix Socket and LiveView configuration.
import { Socket } from "phoenix";
import { LiveSocket } from "phoenix_live_view";
import { hooks as colocatedHooks } from "phoenix-colocated/liberties";
import { Hooks as BackpexHooks } from "backpex";
import topbar from "../vendor/topbar";
import { ScrollSpy } from "./hooks/scroll_spy";

const csrfToken = document
  .querySelector("meta[name='csrf-token']")
  .getAttribute("content");

// Make Backpex table rows clickable (navigate to show on row click)
document.addEventListener("click", (e) => {
  // Only handle clicks on Backpex table rows
  const row = e.target.closest("tr[id^='row-item-']");
  if (!row) return;

  // Don't navigate if clicking on buttons, inputs, or links
  if (e.target.closest("button, input, a, .checkbox")) return;

  // Find the show button in this row and click it
  const showBtn = row.querySelector('[id^="item-action-show-"]');
  console.log("Row click:", row.id, "showBtn:", showBtn?.id || "not found");
  if (showBtn) {
    showBtn.click();
  }
});

const liveSocket = new LiveSocket("/live", Socket, {
  longPollFallbackMs: 2500,
  params: { _csrf_token: csrfToken },
  hooks: { ...colocatedHooks, ...BackpexHooks, ScrollSpy },
});

// Show progress bar on live navigation and form submits
topbar.config({ barColors: { 0: "#29d" }, shadowColor: "rgba(0, 0, 0, .3)" });
window.addEventListener("phx:page-loading-start", (_info) => topbar.show(300));
window.addEventListener("phx:page-loading-stop", (_info) => topbar.hide());

// Scroll to top when navigating (handles same-page navigation)
window.addEventListener("phx:scroll-to-top", () => {
  window.scrollTo({ top: 0, behavior: "instant" });
});

// Hide navbar on scroll down, show on scroll up (mobile-friendly)
let lastScrollY = 0;
let ticking = false;

function updateNavbar() {
  const navbar = document.querySelector(".app-navbar");
  const sectionNav = document.querySelector(".app-section-nav");
  if (!navbar) return;

  const currentScrollY = window.scrollY;
  const scrollingDown = currentScrollY > lastScrollY;
  const pastThreshold = currentScrollY > 100; // Only hide after scrolling 100px

  if (scrollingDown && pastThreshold) {
    navbar.classList.add("navbar-hidden");
    if (sectionNav) sectionNav.classList.add("navbar-hidden");
  } else {
    navbar.classList.remove("navbar-hidden");
    if (sectionNav) sectionNav.classList.remove("navbar-hidden");
  }

  lastScrollY = currentScrollY;
  ticking = false;
}

window.addEventListener("scroll", () => {
  if (!ticking) {
    requestAnimationFrame(updateNavbar);
    ticking = true;
  }
}, { passive: true });

// connect if there are any LiveViews on the page
liveSocket.connect();

// expose liveSocket on window for web console debug logs and latency simulation:
// >> liveSocket.enableDebug()
// >> liveSocket.enableLatencySim(1000)  // enabled for duration of browser session
// >> liveSocket.disableLatencySim()
window.liveSocket = liveSocket;

// The lines below enable quality of life phoenix_live_reload
// development features:
//
//     1. stream server logs to the browser console
//     2. click on elements to jump to their definitions in your code editor
//
if (process.env.NODE_ENV === "development") {
  window.addEventListener(
    "phx:live_reload:attached",
    ({ detail: reloader }) => {
      // Enable server log streaming to client.
      // Disable with reloader.disableServerLogs()
      reloader.enableServerLogs();

      // Open configured PLUG_EDITOR at file:line of the clicked element's HEEx component
      //
      //   * click with "c" key pressed to open at caller location
      //   * click with "d" key pressed to open at function component definition location
      let keyDown;
      window.addEventListener("keydown", (e) => (keyDown = e.key));
      window.addEventListener("keyup", (_e) => (keyDown = null));
      window.addEventListener(
        "click",
        (e) => {
          if (keyDown === "c") {
            e.preventDefault();
            e.stopImmediatePropagation();
            reloader.openEditorAtCaller(e.target);
          } else if (keyDown === "d") {
            e.preventDefault();
            e.stopImmediatePropagation();
            reloader.openEditorAtDef(e.target);
          }
        },
        true,
      );

      window.liveReloader = reloader;
    },
  );
}
